import { CARD_VALUES } from '@/config/cards';
import { ECONOMY } from '@/config/economy';
import { PLAYER } from '@/config/player';
import type { CardDef } from '@/upgrades/cards';

/**
 * The BIG signed number on a card face (docs/03 wave 4 "4-year-old rule"):
 * what the card changes, readable without words. `up` is the number's
 * direction (▲/▼); `good` says whether that direction helps (a cheaper
 * reroll is ▼ but good). Values come straight from CARD_VALUES / ECONOMY.
 */
export interface Headline {
  text: string;
  /** Glyph of the stat this line is about (trade-offs: up and down rows) */
  icon: string;
  up: boolean;
  good: boolean;
}

const pct = (mul: number): number => Math.round((mul - 1) * 100);
const add = (v: number): number => Math.round(v * 100);

function line(icon: string, n: number, unit = '%', goodWhenUp = true): Headline {
  const up = n >= 0;
  return { text: `${up ? '+' : ''}${n}${unit}`, icon, up, good: up === goodWhenUp };
}

/**
 * Lines for a card (1, or 2 for trade-offs / dual buffs); [] = glyph only.
 * @param level the stack level the face shows (tiered cards read it)
 */
export function cardHeadlines(card: CardDef, level: number): Headline[] {
  const V = CARD_VALUES;
  const cat = card.id.match(/^(cat|rate)-/);
  if (cat) return [line(card.icon, pct(cat[1] === 'cat' ? V.catDamage : V.catFireRate))];
  switch (card.id) {
    case 'damage': return [line('damage', pct(V.damage))];
    case 'firerate': return [line('firerate', pct(V.fireRate))];
    case 'projspeed': return [line('projspeed', pct(V.projSpeed))];
    case 'range': return [line('range', pct(V.range))];
    case 'critchance': return [line('critchance', add(V.critChance))];
    case 'critdamage': return [line('critdamage', add(V.critDamage))];
    case 'speed': return [line('speed', pct(V.speed))];
    case 'dashcd': return [line('dashcd', pct(V.dashCooldown), '%', false)];
    case 'magnet': return [line('magnet', pct(V.magnet))];
    case 'greed': return [line('greed', pct(V.greed))];
    case 'discount': return [line('discount', pct(V.discount), '%', false)];
    case 'supershot': return [line('supershot', pct(V.supershot))];
    case 'iframes': return [line('iframes', pct(V.iframes))];
    case 'bulwark': return [line('bulwark', pct(V.bulwark), '%', false)];
    case 'overcharge': return [line('overcharge', pct(V.overcharge))];
    case 'lifesteal': return [line('lifesteal', add(V.lifesteal))];
    case 'platingdeal': return [line('shieldstart', -25, '%', false)];
    case 'heart': return [line('heart', PLAYER.cellHp, '')];
    case 'energy': case 'focus': case 'shieldstart': case 'regen': case 'salvager':
    case 'cargo': case 'barrel': case 'rack':
      return [line(card.icon, 1, '')];
    case 'superfan': return [line('superfan', 2, '')];
    case 'ghostdash': return [{ ...line('ghostdash', V.ghostIframes, 's'), text: `+${V.ghostIframes}s` }];
    case 'rerolldiscount': {
      const steps = ECONOMY.rerollDiscountByStack;
      const mul = steps[Math.min(level, steps.length - 1)]!;
      return [line('rerolldiscount', pct(mul), '%', false)];
    }
    case 'apex': return [line('critchance', add(V.apexCritChance)), line('critdamage', add(V.apexCritDamage))];
    case 'glasscannon':
      return [line('damage', pct(V.glasscannon.damage)), line('iframes', pct(V.glasscannon.iframes))];
    case 'hairtrigger':
      return [line('firerate', pct(V.hairtrigger.fireRate)), line('range', pct(V.hairtrigger.range))];
    case 'heavyplating': return [line('shieldstart', 1, ''), line('speed', pct(V.heavyplating.speed))];
    case 'boosterrig':
      return [line('speed', pct(V.boosterrig.speed)), line('damage', pct(V.boosterrig.damage))];
    case 'lootballast':
      return [line('greed', pct(V.lootballast.greed)), line('speed', pct(V.lootballast.speed))];
    default: return [];
  }
}
